/**
 * Employees Query Hooks — cached-instant rendering for EmployeesScreen.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { employeesApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

interface EmployeesListParams {
  page?: number;
  pageSize?: number;
  department_id?: number;
  organizational_unit?: number;
  employee_type?: string;
  is_active?: boolean;
  search?: string;
  year?: number;
}

interface EmployeesListResponse {
  results: any[];
  count: number;
}

export function useEmployeesQuery(
  params?: EmployeesListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.employees.list(params),
    queryFn: async (): Promise<EmployeesListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.department_id) queryParams.department_id = params.department_id;
      if (params?.organizational_unit)
        queryParams.organizational_unit = params.organizational_unit;
      if (params?.employee_type) queryParams.employee_type = params.employee_type;
      if (params?.is_active !== undefined) queryParams.is_active = params.is_active;
      if (params?.search) queryParams.search = params.search;
      if (params?.year) queryParams.year = params.year;

      return await employeesApi.list(queryParams);
    },
    enabled: options?.enabled ?? true,
    staleTime: STALE_TIMES.GOALS,
    // Keep the prior page visible while an org/filter switch refetches —
    // avoids flashing the full-page skeleton on every dropdown change.
    placeholderData: keepPreviousData,
  });
}

export function useEmployeeDetailQuery(employeeId: number) {
  return useQuery({
    queryKey: queryKeys.employees.detail(employeeId),
    queryFn: () => employeesApi.get(employeeId),
    enabled: !!employeeId,
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useDeleteEmployeeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeId: number) => employeesApi.delete(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });
    },
  });
}

export default useEmployeesQuery;
